import { useState, useEffect, useRef, useMemo } from 'react';
import { useGameState } from '../../../net/GameProvider.js';
import { useI18n } from '../../../i18n.js';
import { getUserAvatar } from '../../../utils/avatar.js';
import { Icon } from '../../Icon.js';
import { ReactionBubble, ReactionPicker } from '../../ReactionUI.js';
import { Opponents } from '../Opponents.js';
import { MyHand } from '../MyHand.js';
import { TwentyOneTable } from './TwentyOneTable.js';
import { TwentyOneControls } from './TwentyOneControls.js';
import { PauseOverlay } from '../common/PauseOverlay.js';
import { TurnIndicator } from '../common/TurnIndicator.js';
import { playCardSelectSound, playYourTurnSound, playTimerRedTick } from '../../../sound.js';
import { trackEvent } from '../../../analytics.js';
import { sortBySuit, sortByRank } from '@big2/shared';
import { TableInteractionsProvider } from '../TableInteractions.js';

export function TwentyOneGameScreen({ onOpenProfile }: { onOpenProfile?: () => void }) {
  const { state, actions } = useGameState();
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const { game, reactions } = state;

  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'rank' | 'suit'>('rank');
  const [sortVersion, setSortVersion] = useState<number>(0);
  const prevTurnPlayerRef = useRef<string | null>(null);
  const lastRedTickSecRef = useRef<number | null>(null);

  const twentyone = game?.twentyone ?? null;
  const myPlayerId = game?.myPlayerId ?? '';
  const mySeat = game?.mySeat ?? 0;
  const isBanker = mySeat === twentyone?.bankerSeat;
  const myTwentyOneScore = twentyone?.myScore ?? 0;
  const myTotalScore = game?.totals?.[myPlayerId] ?? 100;
  const isMyTurn = !!game && game.activePlayerId === game.myPlayerId;
  const pause = game?.pause ?? null;
  const isPaused = pause?.status === 'paused';
  const isRequestingPause = pause?.status === 'requesting';

  const maxBet = useMemo(() => {
    if (!twentyone) return 0;
    const chips = twentyone.scores[myPlayerId] ?? 100;
    return Math.max(0, Math.min(chips, twentyone.bankPot));
  }, [twentyone, myPlayerId]);

  const [betAmount, setBetAmount] = useState<number>(10);

  useEffect(() => {
    if (betAmount > maxBet && maxBet > 0) {
      setBetAmount(maxBet);
    } else if (betAmount < 1 && maxBet > 0) {
      setBetAmount(Math.min(10, maxBet));
    }
  }, [maxBet, betAmount]);

  // Clear selection if hand changes or when paused
  useEffect(() => {
    setSelectedCards(new Set());
  }, [game?.myHand.length, game?.pause?.status]);

  // Play chime when it becomes our turn
  useEffect(() => {
    if (!game || isPaused) return;
    if (game.activePlayerId !== prevTurnPlayerRef.current) {
      prevTurnPlayerRef.current = game.activePlayerId;
      lastRedTickSecRef.current = null;
      if (game.activePlayerId === game.myPlayerId) {
        playYourTurnSound();
      }
    }
  }, [game?.activePlayerId, game?.myPlayerId, isPaused]);

  // Timer rendering + red timer warning sound
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!game?.timeoutEndsAt || isPaused) {
      setTimeLeft(null);
      lastRedTickSecRef.current = null;
      return;
    }
    const tick = () => {
      const serverNow = Date.now() + (state.serverOffsetMs ?? 0);
      const remain = Math.max(0, game.timeoutEndsAt! - serverNow);
      setTimeLeft(remain);
      if (game.activePlayerId === game.myPlayerId && remain > 0 && remain <= 5000) {
        const sec = Math.ceil(remain / 1000);
        if (lastRedTickSecRef.current !== sec) {
          lastRedTickSecRef.current = sec;
          playTimerRedTick(sec);
        }
      }
    };
    tick();
    const iv = setInterval(tick, 100);
    return () => clearInterval(iv);
  }, [game?.timeoutEndsAt, game?.activePlayerId, game?.myPlayerId, isPaused, state.serverOffsetMs]);

  if (!game || !twentyone) return null;

  const mustPlay = isBanker && myTwentyOneScore <= 16;

  let playError: string | null = null;
  if (isMyTurn) {
    if (twentyone.stage === 'betting') {
      if (betAmount < 1 || betAmount > maxBet) {
        playError = isMn ? `Бооцоо 1-ээс ${maxBet} хооронд байна` : `Bet must be 1 to ${maxBet}`;
      }
    } else if (twentyone.stage === 'playing' || twentyone.stage === 'banker') {
      if (isBanker && twentyone.stage === 'banker' && myTwentyOneScore >= 17) {
        playError = isMn ? 'Банкир 17 дээр заавал зогсоно' : 'Banker must stand on 17+';
      } else if (
        myTwentyOneScore >= 21 ||
        twentyone.bustedPlayerIds.includes(myPlayerId) ||
        twentyone.stoodPlayerIds.includes(myPlayerId)
      ) {
        playError = isMn ? '21 оноонд хүрсэн эсвэл дууссан' : 'Already at 21 or turn finished';
      }
    }
  }

  const handlePlay = () => {
    if (isMyTurn && !playError && !isPaused) {
      if (twentyone.stage === 'betting') {
        actions.playCards(betAmount as any);
      } else {
        actions.playCards([]);
      }
    }
  };

  const handlePass = () => {
    if (isMyTurn && !mustPlay && !isPaused) {
      actions.passTurn();
    }
  };

  const playLabel = isBanker
    ? isMn ? 'Хөзөр татах (<=16)' : 'Hit (<=16)'
    : isMn ? 'Хөзөр татах' : 'Hit';

  const passLabel = isBanker
    ? isMn ? 'Зогсох (>=17)' : 'Stand (>=17)'
    : isMn ? 'Зогсох' : 'Stand';

  const toggleCard = (card: string) => {
    setSelectedCards((prev) => {
      const next = new Set(prev);
      const isSelecting = !next.has(card);
      playCardSelectSound(isSelecting);
      if (isSelecting) next.add(card);
      else next.delete(card);
      return next;
    });
  };

  const handleSort = (mode: 'rank' | 'suit') => {
    setSortBy(mode);
    setSortVersion((v) => v + 1);
    trackEvent('hand_sort_changed', { sort_by: mode, game_type: 'twentyone' });
  };

  const sortedHand = sortBy === 'suit' ? sortBySuit(game.myHand) : sortByRank(game.myHand);
  const myPlayerName = game.players.find((p) => p.playerId === game.myPlayerId)?.name ?? '';
  const myAvatar = getUserAvatar(state.profile?.id || game.myPlayerId, myPlayerName);
  const myReaction = reactions[game.myPlayerId];
  const activePlayerName = game.players.find((p) => p.playerId === game.activePlayerId)?.name ?? '';

  return (
    <TableInteractionsProvider onOpenProfile={onOpenProfile}>
      <div className={`game-screen${isMyTurn ? ' my-turn' : ''}${isPaused ? ' is-game-paused' : ''}`}>
        {/* Table Area */}
        <div className="game-table-area">
          <Opponents />
        <PauseOverlay />
        <TwentyOneTable isPaused={isPaused} />

        {/* Local Player Floating Reaction Bubble */}
        {myReaction && (
          <div className="my-reaction-slot">
            <ReactionBubble
              reactionId={myReaction.reactionId}
              seq={myReaction.seq}
              playerName={myPlayerName}
            />
          </div>
        )}

        <TurnIndicator
          isMyTurn={isMyTurn}
          timeLeft={timeLeft}
        />
      </div>

      {/* Player Area */}
      <div className="player-area">
        <div className="sr-only">
          <span role="status">
            {isPaused
              ? isMn ? 'Тоглоом түр зогссон' : 'Game paused'
              : isMyTurn
              ? t.yourTurn
              : t.waitingForTurn(activePlayerName)}
          </span>
        </div>

        {/* Twenty-One Controls */}
        <TwentyOneControls
          isMyTurn={isMyTurn}
          isPaused={isPaused}
          stage={twentyone.stage}
          isBanker={isBanker}
          betAmount={betAmount}
          maxBet={maxBet}
          setBetAmount={setBetAmount}
          playError={playError}
          mustPlay={mustPlay}
          playLabel={playLabel}
          passLabel={passLabel}
          onPass={handlePass}
          onPlay={handlePlay}
        />

        {/* Above cards: Main player's centered status bar */}
        <div className="hand-header-bar">
          <div
            className={`self-counter${state.profile?.tier === 'paid' ? ' is-vip' : ''}`}
            data-seat={game?.mySeat}
            title={isMyTurn ? t.yourTurn : myPlayerName}
          >
            <button
              className="icon-button pause-action-button"
              disabled={isPaused || (isRequestingPause && !!pause?.pauseAgreedPlayerIds.includes(game.myPlayerId))}
              onClick={() => actions.pauseAction(isRequestingPause ? 'agree_pause' : 'request')}
              aria-label={isMn ? 'Тоглоом түр зогсоох' : 'Pause game'}
              title={isMn ? 'Тоглоом түр зогсоох' : 'Pause game'}
              data-tooltip={isMn ? 'Тоглоом түр зогсоох' : 'Pause game'}
            >
              <Icon name="pause" size={14} />
            </button>
            <span
              className="self-player-avatar"
              style={{
                background: myAvatar.gradient,
                borderColor: myAvatar.borderColor,
                boxShadow: `0 0 8px ${myAvatar.glow}`,
              }}
              title={myPlayerName}
              aria-hidden="true"
            >
              {myAvatar.initial}
            </span>
            <span className="self-player-name">{myPlayerName}</span>
            <span
              className="self-card-ratio"
              title={`${game.myHand.length} / ${game.format.cardsEach} ${isMn ? 'хөзөр' : 'cards'}`}
            >
              <Icon name="cards" size={13} />
              <strong>{game.myHand.length}</strong>
              <span className="card-ratio-total">/{game.format.cardsEach}</span>
            </span>
            <ReactionPicker onSelect={actions.sendReaction} onOpenProfile={onOpenProfile} direction="up" compact />
            <strong
              title={isMn ? 'Таны оноо' : 'Your Score'}
              style={{
                color:
                  twentyone.golden21PlayerIds.includes(game.myPlayerId)
                    ? '#fbbf24'
                    : twentyone.bustedPlayerIds.includes(game.myPlayerId)
                    ? '#f87171'
                    : '#34d399',
              }}
            >
              {twentyone.golden21PlayerIds.includes(game.myPlayerId)
                ? '✨ Алтан 21'
                : twentyone.bustedPlayerIds.includes(game.myPlayerId)
                ? `💥 ${myTwentyOneScore}`
                : `🎯 ${myTwentyOneScore}`}
            </strong>
            <span title={isMn ? 'Чип' : 'Chips'}>
              💰 {myTotalScore}
            </span>
            {isBanker ? (
              <span className="hand-order-badge is-dealer" title={isMn ? 'Банкир' : 'Banker'}>
                👑 D · {isMn ? 'Банкир' : 'Banker'}
              </span>
            ) : twentyone.bets[game.myPlayerId] ? (
              <span title={isMn ? 'Бооцоо' : 'Bet'} style={{ color: '#fef08a' }}>
                🪙 {twentyone.bets[game.myPlayerId]}
              </span>
            ) : null}
          </div>
        </div>

        {/* Hand with Ordering Controls */}
        <MyHand
          isPaused={isPaused}
          isMyTurn={isMyTurn}
          sortedHand={sortedHand}
          selectedCards={selectedCards}
          toggleCard={toggleCard}
          sortBy={sortBy}
          sortVersion={sortVersion}
          arrangeControls={
            <div className="hand-arrange-controls">
              <div className="sort-segment" role="group" aria-label={t.sortLabel}>
                <button
                  type="button"
                  className={`sort-btn${sortBy === 'rank' ? ' is-active' : ''}`}
                  onClick={() => handleSort('rank')}
                  aria-pressed={sortBy === 'rank'}
                  aria-label={t.sortByRank}
                  title={t.sortByRank}
                >
                  {isMn ? 'Тоо' : 'Rank'}
                </button>
                <button
                  type="button"
                  className={`sort-btn${sortBy === 'suit' ? ' is-active' : ''}`}
                  onClick={() => handleSort('suit')}
                  aria-pressed={sortBy === 'suit'}
                  aria-label={t.sortBySuit}
                  title={t.sortBySuit}
                >
                  {isMn ? 'Өнгө' : 'Suit'}
                </button>
              </div>
              {selectedCards.size > 0 && !isPaused ? (
                <button
                  className="icon-button clear-selection-button"
                  onClick={() => {
                    trackEvent('hand_selection_cleared', {
                      cleared_count: selectedCards.size,
                      game_type: 'twentyone',
                    });
                    setSelectedCards(new Set());
                  }}
                  aria-label={isMn ? 'Сонголт цуцлах' : 'Clear selection'}
                  title={isMn ? 'Сонголт цуцлах' : 'Clear selection'}
                  data-tooltip={isMn ? 'Сонголт цуцлах' : 'Clear selection'}
                >
                  <Icon name="x" size={18} />
                </button>
              ) : (
                <div className="clear-selection-slot" aria-hidden="true" />
              )}
            </div>
          }
        />
      </div>
    </div>
    </TableInteractionsProvider>
  );
}
